import { batch } from 'react-redux';
import { createSelector, Dictionary } from '@reduxjs/toolkit';
import { selectResources } from '@ff-os/pdp-shared-redux/slices/resourcesSlice';
import { isAiStudioEnabledByTenant, selectTenantId } from '@ff-os/pdp-shared-redux/slices/tenantSlice';
import { applyPalette } from '@ff-os/rendering-shared/coloring';
import { toList } from '@ff-os/shared-utils/dictionaries';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import { unwrap } from '@ff-os/shared-utils/unwrap';
import { createUserInputsPatch } from '@ff-resource-plugins/prefabs-shared/merge-logic/createUserInputsPatch';
import { Prefab } from '@ff-resource-plugins/prefabs-shared/Prefab';
import { selectActiveVariant, selectActiveVariantId } from '@ff-scene-plugins/gift-redux/selectors/variantSelectors';
import { actions as inputActions } from '@ff-scene-plugins/gift-redux/slices/inputsSlice';
import { actions as variableActions, selectPinnedVariables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState, GiftPdpThunk } from '@ff-scene-plugins/gift-redux/store';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { selectActivePrefabId, updateCommission, updatePrefabId, updateViewboxHeight, updateViewboxWidth } from './_redux-meta';
import { createPrefabPreviewPaintables } from './preview/helpers/createPrefabPreviewPaintables';

export const selectPrefabs = selectResources<Prefab>('prefab');

export const filterPrefabs = (
    prefabs: Dictionary<Prefab>,
    tenantId: string,
    aiStudioSupport: boolean,
    variantId: string,
    activePrefabId?: string
) => {
    return toList(prefabs)
        .filter(p => {
            const entities = Object.values(p.variables.entities);
            const hasAiStudioOption = entities.some(e => e && e.options.enableAiStudio);
            return hasAiStudioOption ? aiStudioSupport : true;
        })
        .filter(p => !p.excludedFromVariants?.includes(variantId) || p.id === activePrefabId)
        .filter(p => !p.exclusiveTo?.length || p.exclusiveTo.includes(tenantId));
};

export const selectFilteredPrefabs = createSelector([
    selectPrefabs,
    selectTenantId,
    isAiStudioEnabledByTenant,
    selectActiveVariantId,
    selectActivePrefabId
], filterPrefabs);

export const selectHasMultiplePrefabs = createSelector(
    selectFilteredPrefabs,
    prefabs => prefabs.length > 1);

export const selectActivePrefab = createSelector([
    selectPrefabs,
    selectActivePrefabId
], (prefabs, id) => toList(prefabs).find(p => p.id === id));

export const selectIsActivePrefab = createSelector([
    selectActivePrefabId,
    (state: GiftPdpState, prefabId: string) => prefabId
], (activePrefabId, prefabId) => activePrefabId === prefabId);

const getVariantMatchIds = (state: GiftPdpState): Set<string> => {
    const variantMatchIds = new Set<string>();
    const variants = toList(state.scene.options.variants.entities) as GiftJson.Variant<any>[];
    for (const variant of variants) {
        if (variant?.match) {
            for (const inputId of Object.keys(variant.match)) {
                variantMatchIds.add(inputId);
            }
        }
    }
    return variantMatchIds;
};

const getPreservedVariablesAndInputs = (
    state: GiftPdpState,
    prefabVariableIds: Set<string>,
    variantMatchIds: Set<string>
) => {
    const preservedVariables: GiftJson.Variable<any>[] = [];
    const preservedInputs: Dictionary<GiftJson.Input<any>> = {};

    for (const id of state.project.variables.ids) {
        const variable = state.project.variables.entities[id];
        if (!variable || prefabVariableIds.has(id as string)) {
            continue;
        }

        if (variable.pinned || variantMatchIds.has(id as string)) {
            preservedVariables.push(variable);
            const input = state.project.inputs.entities[id];
            if (input) {
                preservedInputs[id as string] = input;
            }
        }
    }

    return { preservedVariables, preservedInputs };
};

export const applyPrefab = (prefab: Prefab): GiftPdpThunk => (dispatch, getState) => {
    const state = getState();
    const isContentFirst = state.scene?.options?.contentFirst === true;

    const newVariables = unwrap(prefab.variables);
    const patchedInputs = createUserInputsPatch(state.project.inputs, prefab);

    // Apply variantInputs from prefab (e.g., { type: "printed" })
    const variantInputUpdates: GiftJson.Input<any>[] = [];
    if (prefab.variantInputs) {
        for (const [inputId, inputValue] of Object.entries(prefab.variantInputs)) {
            variantInputUpdates.push({ id: inputId, value: inputValue });
        }
    }

    if (isContentFirst) {
        const variantMatchIds = getVariantMatchIds(state);
        const prefabVariableIds = new Set(newVariables.map(v => v.id));
        const { preservedVariables, preservedInputs } = getPreservedVariablesAndInputs(
            state, prefabVariableIds, variantMatchIds
        );

        const allVariables = newVariables.concat(preservedVariables);
        // Apply variantInputs last so they override preserved inputs
        let allInputs = { ...patchedInputs, ...preservedInputs };
        for (const update of variantInputUpdates) {
            const existingInput = allInputs[update.id] || state.project.inputs.entities[update.id];
            if (existingInput) {
                allInputs[update.id] = { ...existingInput, value: update.value };
            } else {
                allInputs[update.id] = update;
            }
        }

        batch(() => {
            dispatch(variableActions.setAll(allVariables));
            dispatch(inputActions.setAll(toList(allInputs)));
            dispatch(updateCommission(prefab.commission?.id));
            dispatch(updatePrefabId(prefab.id));
            dispatch(updateViewboxWidth(prefab.viewbox.width));
            dispatch(updateViewboxHeight(prefab.viewbox.height));
        });
    } else {
        const pinnedVariables = selectPinnedVariables(state);
        const allVariables = newVariables.concat(pinnedVariables);

        // Get variant match inputs (type, material, size, etc.) to preserve
        const variantMatchIds = getVariantMatchIds(state);
        const preservedInputs: Dictionary<GiftJson.Input<any>> = {};
        for (const inputId of variantMatchIds) {
            const input = state.project.inputs.entities[inputId];
            if (input) {
                preservedInputs[inputId] = input;
            }
        }

        // Build final inputs: preserved variant inputs + prefab inputs + variantInputs
        let allInputs = { ...preservedInputs, ...patchedInputs };
        for (const update of variantInputUpdates) {
            const existingInput = allInputs[update.id] || state.project.inputs.entities[update.id];
            if (existingInput) {
                allInputs[update.id] = { ...existingInput, value: update.value };
            } else {
                allInputs[update.id] = update;
            }
        }

        batch(() => {
            // Use setAll to replace inputs/variables completely, removing old prefab data
            dispatch(inputActions.setAll(toList(allInputs)));
            dispatch(variableActions.setAll(allVariables));
            dispatch(updateCommission(prefab.commission?.id));
            dispatch(updatePrefabId(prefab.id));
            dispatch(updateViewboxWidth(prefab.viewbox.width));
            dispatch(updateViewboxHeight(prefab.viewbox.height));
        });
    }
};

export const selectCommissionAssetId = createSelector([
    selectActivePrefab
], prefab => prefab && prefab.commission?.logoAssetId);

export const selectCommissionName = createSelector([
    selectActivePrefab
], prefab => prefab && prefab.commission?.name);

const selectPrefabById = createSelector([
    selectPrefabs,
    (state: GiftPdpState, id: string) => id
], (prefabs, id) => prefabs[id]!);

export const selectPrefabViewboxById = createSelector([
    (state: GiftPdpState, id: string) => selectPrefabById(state, id)
], prefab => prefab.viewbox);

export const selectPaintablesForPrefabPreviewById = createSelector([
    selectPrefabById,
    (s: GiftPdpState) => s.project.inputs,
    (s: GiftPdpState) => selectActiveVariant(s),
    (s: GiftPdpState, prefabId: string, t: Translate) => t,
    (s: GiftPdpState) => s.auth.jwt
], createPrefabPreviewPaintables);

export const selectPreviewBackgroundColor = createSelector([
    (state: GiftPdpState, prefabId: string) => selectPrefabById(state, prefabId),
    (state: GiftPdpState, prefabId: string) => selectActiveVariant(state)
], (prefab: Prefab, variant: GiftJson.Variant<any>) => {

    if (prefab.previewBackgroundColor) {
        return applyPalette(prefab.previewBackgroundColor, variant.palette);
    } else {
        return undefined;
    }
});